<template>
  <main class="activation-handoff min-h-screen px-5 py-12 font-['Poppins'] sm:py-20">
    <section class="mx-auto w-full max-w-[520px] rounded-[28px] border border-white/60 bg-white p-8 text-center shadow-2xl shadow-slate-900/20 sm:p-11">
      <img class="mx-auto h-10 w-auto" :src="platformaLogo" alt="Platforma">

      <template v-if="!errorMessage">
        <div class="mx-auto mt-10 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-[#2D8CFF]">
          <ArrowPathIcon class="h-8 w-8 animate-spin" aria-hidden="true" />
        </div>
        <p class="mt-7 text-xs font-bold uppercase tracking-[0.22em] text-[#2D8CFF]">Account activated</p>
        <h1 class="mt-3 text-2xl font-bold text-slate-950 sm:text-3xl">Opening your dashboard</h1>
        <p class="mx-auto mt-3 max-w-sm text-sm leading-6 text-slate-500">
          We’re securely signing you in. You’ll arrive in your creator dashboard in just a moment.
        </p>
      </template>

      <template v-else>
        <div class="mx-auto mt-10 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
          <ExclamationTriangleIcon class="h-8 w-8" aria-hidden="true" />
        </div>
        <p class="mt-7 text-xs font-bold uppercase tracking-[0.22em] text-amber-600">Account activated</p>
        <h1 class="mt-3 text-2xl font-bold text-slate-950">One more step</h1>
        <p class="mx-auto mt-3 max-w-sm text-sm leading-6 text-slate-600">{{ errorMessage }}</p>
        <RouterLink
          to="/"
          class="mt-7 inline-flex h-12 w-full items-center justify-center rounded-xl bg-[#2D8CFF] px-5 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition hover:bg-[#1f78dc] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-200"
        >
          Sign In to Platforma
        </RouterLink>
      </template>
    </section>
  </main>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import platformaLogo from '@/assets/media/images/platforma-logo-in-color.svg';

const route = useRoute();
const errorMessage = ref('');

const completeSessionHandoff = async () => {
  const token = String(route.query?.token || '').trim();
  window.history.replaceState({}, document.title, '/activation-handoff');

  if (!token) {
    errorMessage.value = 'Your secure sign-in link is missing. Sign in with the password you just created.';
    return;
  }

  try {
    const response = await fetch('/api/auth/creator-activation/session-handoff', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      cache: 'no-store',
      body: JSON.stringify({ token }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) {
      throw new Error(payload?.message || 'Your automatic sign-in could not be completed.');
    }
    const destination = String(payload?.redirectUrl || '/creator/dashboard?welcome=1').trim();
    window.location.replace(destination);
  } catch (error) {
    errorMessage.value = error?.message || 'Your automatic sign-in could not be completed. Sign in with the password you just created.';
  }
};

onMounted(completeSessionHandoff);
</script>

<style scoped>
.activation-handoff {
  background:
    radial-gradient(circle at 12% 15%, rgba(204, 54, 255, 0.26), transparent 34%),
    radial-gradient(circle at 88% 18%, rgba(37, 211, 137, 0.25), transparent 34%),
    linear-gradient(135deg, #6958f5 0%, #2695fb 52%, #23c994 100%);
}
</style>
